// src/components/common/search/SearchPanel.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';

import { defaultIsActive } from './activeFilterCount';
import type { ActiveChipsConfig, SearchChipSpec, SearchPanelAction } from './types';
import { DEFAULT_AUTO_SEARCH_DELAY_MS, DEFAULT_AUTO_SEARCH_ENABLED } from './constants';

export type SearchPanelProps<TSearch extends Record<string, unknown>> = {
  title?: string;

  /**
   * Accordion open state.
   * - Controlled: provide `open` + `onOpenChange`
   * - Uncontrolled: omit `open` and it will manage internal state
   */
  open?: boolean;
  onOpenChange?: (nextOpen: boolean) => void;

  onSearch: () => void;
  onReset: () => void;

  resetDisabled?: boolean;
  searchDisabled?: boolean;

  actions?: SearchPanelAction[];
  footer?: React.ReactNode;

  /**
   * Auto-search configuration.
   * - Default enabled: false (global policy)
   * - Default delayMs: 500 (global policy)
   *
   * NOTE:
   * - Pages can override these defaults via this prop,
   *   but global defaults should live in ./constants.
   */
  autoSearch?: {
    enabled?: boolean;
    delayMs?: number;
  };

  /**
   * Canonical prop for active chips.
   * (We also keep the alias `activeChips` for backward compatibility.)
   */
  chipsConfig?: ActiveChipsConfig<TSearch>;

  /**
   * Alias prop for compatibility with existing usage.
   * If both are provided, `chipsConfig` takes precedence.
   */
  activeChips?: ActiveChipsConfig<TSearch>;

  children: React.ReactNode;
};

/**
 * Create a stable JSON string for change detection (auto-search).
 * - Sorts object keys
 * - Handles circular references
 */
const stableStringify = (value: unknown): string => {
  const seen = new WeakSet<object>();

  const stringify = (x: unknown): unknown => {
    if (x && typeof x === 'object') {
      const obj = x as object;
      if (seen.has(obj)) return '[Circular]';
      seen.add(obj);

      if (Array.isArray(x)) return x.map(stringify);

      const rec = x as Record<string, unknown>;
      const keys = Object.keys(rec).sort();
      const out: Record<string, unknown> = {};
      keys.forEach((k) => {
        out[k] = stringify(rec[k]);
      });
      return out;
    }
    return x;
  };

  return JSON.stringify(stringify(value));
};

/**
 * Default text renderer for chip display when a chip spec does not provide `getValueText`.
 */
const defaultValueText = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (value instanceof Date) return value.toISOString();
  try {
    return String(value);
  } catch {
    return '';
  }
};

type ActiveChipItem<TSearch extends Record<string, unknown>> = {
  spec: SearchChipSpec<TSearch>;
  display: string;
};

/**
 * SearchPanel
 *
 * Shared search container with:
 * - Accordion collapse/expand (controlled or uncontrolled)
 * - Search / Reset buttons (single place)
 * - Optional auto-search (debounced) based on search state changes
 * - Active filter count badge
 * - Active filter chips (collapsible summary + removable chips inside)
 * - Optional actions area (e.g., Export / Create)
 * - Optional footer slot (e.g., loading bar)
 */
function SearchPanel<TSearch extends Record<string, unknown>>(props: SearchPanelProps<TSearch>) {
  const {
    title = 'Filters',
    open,
    onOpenChange,

    onSearch,
    onReset,

    resetDisabled = false,
    searchDisabled = false,

    actions = [],
    footer,
    autoSearch,

    chipsConfig,
    activeChips,

    children,
  } = props;

  /**
   * Resolve the canonical chips config.
   * - Prefer `chipsConfig`
   * - Fallback to `activeChips` for backward compatibility
   */
  const resolvedChipsConfig = chipsConfig ?? activeChips;

  /**
   * Resolve auto-search policy (single source of truth).
   * - default enabled: false
   * - default delayMs: 500
   */
  const resolvedAutoSearch = useMemo(
    () => ({
      enabled: autoSearch?.enabled ?? DEFAULT_AUTO_SEARCH_ENABLED,
      delayMs: autoSearch?.delayMs ?? DEFAULT_AUTO_SEARCH_DELAY_MS,
    }),
    [autoSearch?.enabled, autoSearch?.delayMs],
  );

  /* ------------------------------------------------------------------
   * Accordion open state
   * ------------------------------------------------------------------ */

  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = typeof open === 'boolean' && typeof onOpenChange === 'function';
  const resolvedOpen = isControlled ? (open as boolean) : internalOpen;

  const handleAccordionChange = (_: unknown, expanded: boolean) => {
    if (isControlled) {
      onOpenChange?.(expanded);
      return;
    }
    setInternalOpen(expanded);
    onOpenChange?.(expanded);
  };

  /* ------------------------------------------------------------------
   * Active chips
   * ------------------------------------------------------------------ */

  const activeChipItems = useMemo<ActiveChipItem<TSearch>[]>(() => {
    if (!resolvedChipsConfig?.enabled) return [];

    const { search, chips } = resolvedChipsConfig;

    return chips
      .map((spec) => {
        // Fallback value lookup by spec.key for default heuristics/value text.
        const rawValue = (search as Record<string, unknown>)[spec.key];

        const active = spec.isActive ? spec.isActive(search) : defaultIsActive(rawValue, []);
        if (!active) return null;

        const removable = spec.removable ? spec.removable(search) : true;
        if (!removable) return null;

        const text = spec.getValueText ? spec.getValueText(search) : defaultValueText(rawValue);
        const valueText = String(text ?? '').trim();

        if (!valueText) return { spec, display: spec.label };
        return { spec, display: `${spec.label}: ${valueText}` };
      })
      .filter(Boolean) as ActiveChipItem<TSearch>[];
  }, [resolvedChipsConfig]);

  /**
   * Active filter count is derived from the same logic as active chips:
   * - Active AND removable chips only
   */
  const activeFilterCount = useMemo(() => {
    if (!resolvedChipsConfig?.enabled) return 0;
    return activeChipItems.length;
  }, [resolvedChipsConfig?.enabled, activeChipItems.length]);

  const maxVisible = resolvedChipsConfig?.maxVisible ?? 2;
  const collapsedVisible = activeChipItems.slice(0, maxVisible);
  const collapsedExtra = Math.max(0, activeChipItems.length - collapsedVisible.length);

  const collapsedExtraTooltip = useMemo(() => {
    if (collapsedExtra <= 0) return '';
    return activeChipItems.slice(maxVisible).map((x) => x.display).join('\n');
  }, [activeChipItems, collapsedExtra, maxVisible]);

  /* ------------------------------------------------------------------
   * Auto-search (debounced)
   * ------------------------------------------------------------------ */

  const lastSearchKeyRef = useRef<string>('');
  const autoTimerRef = useRef<number | null>(null);

  /**
   * A stable key representing the current search state.
   * Used to detect real changes and trigger auto-search (debounced).
   */
  const searchKey = useMemo(() => {
    if (!resolvedAutoSearch.enabled || !resolvedChipsConfig?.enabled) return '';
    return stableStringify(resolvedChipsConfig.search);
  }, [resolvedAutoSearch.enabled, resolvedChipsConfig?.enabled, resolvedChipsConfig?.search]);

  /**
   * Safety: if auto-search gets disabled (or chips config becomes disabled),
   * cancel any pending debounce timer immediately.
   */
  useEffect(() => {
    if (resolvedAutoSearch.enabled && resolvedChipsConfig?.enabled) return;

    if (autoTimerRef.current) {
      window.clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, [resolvedAutoSearch.enabled, resolvedChipsConfig?.enabled]);

  useEffect(() => {
    if (!resolvedAutoSearch.enabled) return;
    if (!resolvedChipsConfig?.enabled) return;

    // First mount (or first time enabling): initialize key without firing.
    if (!lastSearchKeyRef.current) {
      lastSearchKeyRef.current = searchKey;
      return;
    }

    // Dedupe: do nothing if the search state did not change.
    if (lastSearchKeyRef.current === searchKey) return;
    lastSearchKeyRef.current = searchKey;

    // Debounce: clear previous timer before scheduling the next search.
    if (autoTimerRef.current) window.clearTimeout(autoTimerRef.current);

    autoTimerRef.current = window.setTimeout(() => {
      onSearch();
    }, resolvedAutoSearch.delayMs);

    return () => {
      if (autoTimerRef.current) window.clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    };
  }, [
    resolvedAutoSearch.enabled,
    resolvedAutoSearch.delayMs,
    resolvedChipsConfig?.enabled,
    searchKey,
    onSearch,
  ]);

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Accordion
        expanded={resolvedOpen}
        onChange={handleAccordionChange}
        elevation={0}
        sx={{ backgroundColor: 'transparent' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 0,
            minHeight: 40,
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 1,
              my: 0.5,
            },
          }}
        >
          <Badge
            color="primary"
            badgeContent={activeFilterCount}
            invisible={activeFilterCount <= 0}
            sx={{ '& .MuiBadge-badge': { fontWeight: 800 } }}
          >
            <TuneIcon fontSize="small" />
          </Badge>

          <Typography sx={{ fontWeight: 800 }}>{title}</Typography>

          {/* Collapsed summary chips */}
          {!resolvedOpen && resolvedChipsConfig?.enabled && activeChipItems.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ ml: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {collapsedVisible.map((item) => (
                <Chip key={item.spec.key} size="small" label={item.display} />
              ))}

              {collapsedExtra > 0 && (
                <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{collapsedExtraTooltip}</pre>}>
                  <Chip size="small" variant="outlined" label={`+${collapsedExtra}`} />
                </Tooltip>
              )}
            </Stack>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ px: 0, pt: 1 }}>
          {children}

          {/* Expanded active chips */}
          {resolvedChipsConfig?.enabled && activeChipItems.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
              {activeChipItems.map((item) => (
                <Chip
                  key={item.spec.key}
                  size="small"
                  label={item.display}
                  onDelete={() => {
                    item.spec.clear({
                      search: resolvedChipsConfig.search,
                      setValue: resolvedChipsConfig.setValue,
                    });
                    resolvedChipsConfig.onAfterClear?.();
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Actions row */}
          <Box mt={3} display="flex" gap={1.5} flexWrap="wrap" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={onSearch}
              disabled={searchDisabled}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={onReset}
              disabled={resetDisabled}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Reset
            </Button>

            {actions.map((a) => (
              <Button
                key={a.key}
                variant={a.variant ?? 'outlined'}
                color={a.color ?? 'primary'}
                startIcon={a.startIcon}
                onClick={a.onClick}
                disabled={a.disabled}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {a.label}
              </Button>
            ))}
          </Box>

          {footer ? <Box mt={2}>{footer}</Box> : null}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}

export default SearchPanel;
